import 'remark-github-alerts/styles/github-base.css';
import 'remark-github-alerts/styles/github-colors-light.css';
import 'remark-github-alerts/styles/github-colors-dark-class.css';
import '@/styles/syntax-highlight.css';

import React, { useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import remarkGithubAlerts from 'remark-github-alerts';

import { CopyableTable } from '@/lib/base/table/CopyableTable';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { parseAhoyUri } from '@/lib/crm/utils/parseAhoyUri';
import { logger } from '@/lib/logger';
import {
  createEntityMarkdownComponents,
  entitySanitizeSchema,
  preprocessEntityTags,
} from '@/lib/markdown/entityRendering';
import { rehypeEscapeUnknownRawTags } from '@/lib/markdown/rehypeEscapeUnknownRawTags';

/**
 * Decode HTML entities (&lt; &gt; &amp;) in markdown content to a bounded fixpoint.
 *
 * CRM string properties use `validate_no_html` which rejects raw angle
 * brackets, so stored markdown encodes them as entities.  We decode here so
 * that entity-mention tags (stored as `&lt;Entity …&gt;`) can be recognized
 * and rendered as chips.  Legacy stored content can be multiply-escaped
 * (e.g. `&amp;amp;lt;` from escape accumulation across reasoning cycles), so
 * we repeat the same replacements until the string stops changing, bounded to
 * avoid pathological loops. This is behavior-identical to a single pass for
 * correctly-stored single-escaped/unescaped content. The decoded output
 * always flows through the raw-HTML pipeline, but `rehypeEscapeUnknownRawTags`
 * decides per tag what renders: decoded SAFE tags (`&lt;b&gt;` → `<b>`) render
 * as HTML, while decoded pseudo-tags (`&lt;string&gt;` → `<string>`) are
 * re-escaped back to literal text before the HTML parser sees them.
 */
function decodeHtmlEntities(md: string): string {
  // `&amp;` is decoded LAST each pass so a legitimate single `&` is never over-stripped.
  let prev = md;
  for (let i = 0; i < 10; i += 1) {
    const next = prev
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
    if (next === prev) {
      return next;
    }
    prev = next;
  }
  return prev;
}

interface MarkdownContentProps {
  content: string;
  className?: string;
  /** Override or extend default markdown component renderers. Merged with defaults (overrides win). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- react-markdown component type uses complex generics not worth matching
  components?: Record<string, React.ComponentType<any>>;
  /** Additional remark plugins (remarkGfm is always included) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- react-markdown plugin types use complex generics not worth matching
  remarkPlugins?: any[];
  /** Rehype plugins (none by default) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- react-markdown plugin types use complex generics not worth matching
  rehypePlugins?: any[];
  /** Custom URL transform function */
  urlTransform?: (url: string) => string;
  /** Size variant — 'compact' uses smaller typography for sidebar/step contexts */
  size?: 'default' | 'compact';
  /** @deprecated Entity rendering is always on; this prop is ignored. */
  entityRendering?: boolean;
  /** Org ID — used for entity link/badge resolution. */
  orgId?: number;
  /** Wrapper element tag — 'div' (default) for block contexts, 'span' for inline phrasing contexts (e.g. inside headings) */
  as?: 'div' | 'span';
  /**
   * When `false`, entity badges render non-interactively (no nested
   * focusable/clickable control, no aria-label override) — use inside
   * `<legend>`/`<label>` phrasing content, where a nested `role="button"`
   * tabbable element would add a spurious tab stop and corrupt the
   * surrounding control's accessible name. Defaults to `true`.
   */
  entityInteractive?: boolean;
  /**
   * Meeting-slot ids pre-resolved to times (from the rendering action's
   * `enriched_data.resolved_slots`). Matching `<meeting-slot>` refs render
   * immediately with no network request; unmatched refs fall back to the
   * async resolve endpoint.
   */
  resolvedSlots?: Record<string, { start: string; end: string; label: string }>;
}

interface MarkdownComponentProps {
  children?: React.ReactNode;
}

interface CodeComponentProps extends MarkdownComponentProps {
  className?: string;
}

interface AnchorProps extends MarkdownComponentProps {
  href?: string;
}

/**
 * Validates URLs to prevent XSS and open redirect attacks.
 * Allows: https:, http:, mailto:, relative paths (/path), anchors (#id), valid ahoy: URIs.
 * Rejects: javascript:, data:, file:, protocol-relative URLs (//), invalid ahoy: URIs, and other potentially dangerous protocols.
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper function co-located with component
export const defaultUrlTransform = (url: string): string => {
  const lowerUrl = url.toLowerCase();

  // Allow ONLY valid ahoy: URIs (must parse successfully)
  if (lowerUrl.startsWith('ahoy:')) {
    const parsed = parseAhoyUri(url);
    if (!parsed) {
      logger.warn('Invalid ahoy URI in markdown', {
        url,
        component: 'MarkdownContent',
      });
      return '';
    }
    return url;
  }

  // Allow standard web protocols and relative paths (case-insensitive)
  // Block protocol-relative URLs (//attacker.com) with negative lookahead
  if (/^(https?:|mailto:|\/(?!\/)|#)/i.test(url)) {
    return url;
  }

  // Reject everything else (dangerous protocols)
  return '';
};

const MarkdownAnchor = ({ children, href }: AnchorProps) => {
  // Origin-aware open (see useOpenSlideout): layers only when the link is
  // inside a rendered entity/thread slideout; replaces otherwise.
  const openSlideout = useOpenSlideoutOptional();
  const parsed = parseAhoyUri(href);

  const slideoutId = parsed?.id ?? null;
  const handleSlideoutClick = useCallback(() => {
    if (openSlideout && slideoutId) {
      openSlideout(slideoutId);
    }
  }, [openSlideout, slideoutId]);

  if (parsed && !openSlideout) {
    logger.debug('Ahoy link requires slideout context unavailable', {
      id: parsed.id,
      type: parsed.type,
      component: 'MarkdownContent',
    });
    return (
      <span
        className='opacity-50 cursor-not-allowed'
        role='status'
        aria-label={`${parsed.type} link unavailable`}
        title='CRM slideout context not available'
      >
        {children}
      </span>
    );
  }

  if (parsed && openSlideout) {
    return (
      <button
        type='button'
        onClick={handleSlideoutClick}
        className='text-primary hover:text-primary-focus underline cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded-sm'
        title={`Open ${parsed.type} record`}
      >
        {children}
      </button>
    );
  }

  return (
    <a
      href={href}
      className='text-primary hover:text-primary-focus underline'
      target='_blank'
      rel='noopener noreferrer'
    >
      {children}
    </a>
  );
};

const MdTable = ({ children }: MarkdownComponentProps) => (
  <CopyableTable>{children}</CopyableTable>
);
const MdThead = ({ children }: MarkdownComponentProps) => (
  <thead className='bg-bg-secondary'>{children}</thead>
);
const MdTbody = ({ children }: MarkdownComponentProps) => (
  <tbody className='bg-bg-primary divide-y divide-border-secondary'>
    {children}
  </tbody>
);
const MdTr = ({ children }: MarkdownComponentProps) => (
  <tr className='hover:bg-bg-secondary transition-colors'>{children}</tr>
);
const MdTh = ({ children }: MarkdownComponentProps) => (
  <th className='px-6 py-3 text-left text-xs font-semibold text-fg-quaternary whitespace-nowrap border-b border-border-secondary'>
    {children}
  </th>
);
const MdTd = ({ children }: MarkdownComponentProps) => (
  <td className='px-6 py-4 text-sm text-tertiary'>{children}</td>
);

const SHARED_RENDERERS = {
  a: MarkdownAnchor,
  table: MdTable,
  thead: MdThead,
  tbody: MdTbody,
  tr: MdTr,
  th: MdTh,
  td: MdTd,
};

const CompactH1 = ({ children }: MarkdownComponentProps) => (
  <h1 className='text-sm font-semibold mb-1 first:mt-0 text-fg-primary'>
    {children}
  </h1>
);
const CompactH2 = ({ children }: MarkdownComponentProps) => (
  <h2 className='text-sm font-semibold mb-1 first:mt-0 text-fg-primary'>
    {children}
  </h2>
);
const CompactH3 = ({ children }: MarkdownComponentProps) => (
  <h3 className='text-sm font-semibold mb-1 first:mt-0 text-fg-primary'>
    {children}
  </h3>
);
const CompactP = ({ children }: MarkdownComponentProps) => (
  // leading-6 (24px) accommodates inline MentionBadge pills (~26px tall via
  // BadgeWithIcon md) with align-middle, so the badge bottom doesn't get
  // visually clipped by the line box.  Stays tighter than DefaultP's
  // leading-7 to preserve compact density for plain-text contexts.
  <p className='mb-2 leading-6 first:mt-0 last:mb-0'>{children}</p>
);
const CompactCode = ({ children, className }: CodeComponentProps) => {
  // Block code (inside <pre>) receives className from rehype-highlight
  if (className) {
    return <code className={`${className} text-xs font-mono`}>{children}</code>;
  }
  // Inline code
  return (
    <code className='bg-bg-secondary text-fg-primary px-1 py-0.5 rounded text-xs font-mono'>
      {children}
    </code>
  );
};
const CompactPre = ({ children }: MarkdownComponentProps) => (
  <pre className='bg-bg-secondary text-fg-primary p-3 rounded-lg overflow-x-auto my-2 first:mt-0 text-xs font-mono border border-border-secondary'>
    {children}
  </pre>
);
const CompactUl = ({ children }: MarkdownComponentProps) => (
  <ul className='list-disc pl-5 mb-2 first:mt-0 space-y-1 marker:text-tertiary'>
    {children}
  </ul>
);
const CompactOl = ({ children }: MarkdownComponentProps) => (
  <ol className='list-decimal pl-5 mb-2 first:mt-0 space-y-1 marker:text-tertiary marker:font-medium'>
    {children}
  </ol>
);
const CompactLi = ({ children }: MarkdownComponentProps) => (
  <li className='text-fg-primary pl-1'>{children}</li>
);
const CompactBlockquote = ({ children }: MarkdownComponentProps) => (
  <blockquote className='border-l-2 border-border-secondary pl-3 py-1 mb-2 first:mt-0 text-fg-secondary'>
    {children}
  </blockquote>
);
const CompactStrong = ({ children }: MarkdownComponentProps) => (
  <strong className='font-semibold text-fg-primary'>{children}</strong>
);
const CompactEm = ({ children }: MarkdownComponentProps) => (
  <em className='italic text-fg-primary'>{children}</em>
);

const DefaultH1 = ({ children }: MarkdownComponentProps) => (
  <h1 className='text-xl font-semibold mt-6 mb-3 first:mt-0 text-fg-primary'>
    {children}
  </h1>
);
const DefaultH2 = ({ children }: MarkdownComponentProps) => (
  <h2 className='text-lg font-semibold mt-5 mb-2.5 first:mt-0 text-fg-primary'>
    {children}
  </h2>
);
const DefaultH3 = ({ children }: MarkdownComponentProps) => (
  <h3 className='text-base font-semibold mt-4 mb-2 first:mt-0 text-fg-primary'>
    {children}
  </h3>
);
const DefaultP = ({ children }: MarkdownComponentProps) => (
  <p className='mb-4 first:mt-0 last:mb-0 leading-7'>{children}</p>
);
const DefaultCode = ({ children, className }: CodeComponentProps) => {
  // Block code (inside <pre>) receives className from rehype-highlight
  if (className) {
    return <code className={`${className} text-sm font-mono`}>{children}</code>;
  }
  // Inline code
  return (
    <code className='bg-bg-secondary text-fg-primary px-1.5 py-0.5 rounded text-sm font-mono'>
      {children}
    </code>
  );
};
const DefaultPre = ({ children }: MarkdownComponentProps) => (
  <pre className='bg-bg-secondary text-fg-primary p-4 rounded-lg overflow-x-auto my-4 first:mt-0 text-sm font-mono border border-border-secondary'>
    {children}
  </pre>
);
const DefaultUl = ({ children }: MarkdownComponentProps) => (
  <ul className='list-disc pl-6 mb-4 first:mt-0 space-y-2 marker:text-tertiary'>
    {children}
  </ul>
);
const DefaultOl = ({ children }: MarkdownComponentProps) => (
  <ol className='list-decimal pl-6 mb-4 first:mt-0 space-y-2 marker:text-tertiary marker:font-medium'>
    {children}
  </ol>
);
const DefaultLi = ({ children }: MarkdownComponentProps) => (
  <li className='text-fg-primary pl-1 leading-7'>{children}</li>
);
const DefaultBlockquote = ({ children }: MarkdownComponentProps) => (
  <blockquote className='border-l-4 border-border-secondary pl-4 py-2 my-4 first:mt-0 bg-bg-primary text-fg-secondary'>
    {children}
  </blockquote>
);
const DefaultHr = () => <hr className='my-6 border-border-secondary' />;
const DefaultStrong = ({ children }: MarkdownComponentProps) => (
  <strong className='font-semibold text-fg-primary'>{children}</strong>
);
const DefaultEm = ({ children }: MarkdownComponentProps) => (
  <em className='italic text-fg-primary'>{children}</em>
);

const COMPACT_COMPONENTS = {
  h1: CompactH1,
  h2: CompactH2,
  h3: CompactH3,
  p: CompactP,
  code: CompactCode,
  pre: CompactPre,
  ul: CompactUl,
  ol: CompactOl,
  li: CompactLi,
  blockquote: CompactBlockquote,
  strong: CompactStrong,
  em: CompactEm,
  ...SHARED_RENDERERS,
};

const DEFAULT_COMPONENTS = {
  h1: DefaultH1,
  h2: DefaultH2,
  h3: DefaultH3,
  p: DefaultP,
  code: DefaultCode,
  pre: DefaultPre,
  ul: DefaultUl,
  ol: DefaultOl,
  li: DefaultLi,
  blockquote: DefaultBlockquote,
  hr: DefaultHr,
  strong: DefaultStrong,
  em: DefaultEm,
  ...SHARED_RENDERERS,
};

export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  className = '',
  components: userComponents,
  remarkPlugins: userRemarkPlugins = [],
  rehypePlugins: userRehypePlugins = [],
  urlTransform = defaultUrlTransform,
  size = 'default',
  orgId,
  as: Wrapper = 'div',
  entityInteractive,
  resolvedSlots,
}) => {
  const defaultComponents =
    size === 'compact' ? COMPACT_COMPONENTS : DEFAULT_COMPONENTS;

  // Decode HTML entities, then normalize entity-mention tags into the
  // <span data-entity> form the `span` renderer intercepts.
  const processedContent = useMemo(
    () => preprocessEntityTags(decodeHtmlEntities(content)),
    [content]
  );

  // Build rehype plugin pipeline: raw-HTML handling + entity rendering +
  // syntax highlighting. rehypeHighlight runs LAST so that any sanitize
  // plugins (built-in or caller-provided) cannot strip the
  // <span class="hljs-*"> markup it adds.
  //
  // The raw-HTML pipeline is UNCONDITIONAL, with the decision of what counts
  // as HTML made per tag by rehypeEscapeUnknownRawTags (which must run before
  // rehypeRaw): safe tags (the sanitize allowlist — Plate note-body HTML,
  // formatting, entity spans) parse and render; anything else (`Array<string>`
  // pseudo-tags in AI prose, `<script>`) is escaped to literal text before the
  // HTML parser sees it. Code spans/fences are already `code` elements by this
  // stage and are untouched. This per-tag rule replaced two whole-content
  // gates that each caused a regression (entity-tag gating broke mention-free
  // note bodies; a per-callsite flag made correctness depend on every caller)
  // — see rehypeEscapeUnknownRawTags.ts and MarkdownContent.rawHtmlGate.test.tsx.
  const allRehypePlugins = useMemo(
    () => [
      rehypeEscapeUnknownRawTags,
      rehypeRaw,
      [rehypeSanitize, entitySanitizeSchema],
      ...userRehypePlugins,
      rehypeHighlight,
    ],
    [userRehypePlugins]
  );

  // Build entity components for the markdown renderers.
  const entityComponents = useMemo(
    () =>
      createEntityMarkdownComponents(
        orgId,
        undefined,
        entityInteractive,
        resolvedSlots
      ) as Record<
        string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- react-markdown component type uses complex generics not worth matching
        React.ComponentType<any>
      >,
    [orgId, entityInteractive, resolvedSlots]
  );

  // Merge default components with entity components and user overrides (user overrides win)
  const mergedComponents = useMemo(
    () => ({
      ...defaultComponents,
      ...entityComponents,
      ...userComponents,
    }),
    [defaultComponents, entityComponents, userComponents]
  );

  // Combine remark plugins (remarkGfm always included)
  const allRemarkPlugins = useMemo(
    () => [remarkGfm, remarkGithubAlerts, ...userRemarkPlugins],
    [userRemarkPlugins]
  );

  return (
    <Wrapper className={className}>
      <ReactMarkdown
        remarkPlugins={allRemarkPlugins}
        rehypePlugins={allRehypePlugins}
        urlTransform={urlTransform}
        components={mergedComponents}
      >
        {processedContent}
      </ReactMarkdown>
    </Wrapper>
  );
};
